import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HIERARCHY_PATHS } from "../data/content";
import { SectionHead } from "./Reveal";
import { useI18n } from "../i18n";
import ShareBadge from "./ShareBadge";

export default function HierarchyExplorer() {
  const { tr } = useI18n();
  const [path, setPath] = useState("india");
  const [depth, setDepth] = useState(2);
  const chain = HIERARCHY_PATHS[path].chain;
  const active = chain[depth];

  return (
    <section id="explorer" data-testid="hierarchy-explorer-section" className="py-24 lg:py-32">
      <SectionHead
        index="§ 01"
        eyebrow="Hierarchy Explorer"
        title="Drill from the world down to a single village — live."
        sub="No hard-coded State → District. Every country configures its own administrative terminology across ADM0–ADM5, resolved against immutable location UUIDs."
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14">
        <div className="flex gap-3 mb-10">
          {Object.entries(HIERARCHY_PATHS).map(([key, p]) => (
            <button
              key={key}
              data-testid={`drilldown-tab-${key}`}
              onClick={() => { setPath(key); setDepth(2); }}
              className={`font-mono-wp text-xs uppercase tracking-[0.18em] px-6 py-3 border rounded-xl transition-all duration-300 active:scale-[0.97] ${
                path === key
                  ? "bg-[#2563EB] text-white border-[#2563EB]"
                  : "border-[#BFD4F2] text-[#4A5568] hover:border-[#2563EB] hover:text-[#2563EB]"
              }`}
            >
              {p.label} · {p.domain}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono-wp text-[0.65rem] uppercase tracking-[0.25em] text-[#718096] mb-5">{tr("Select a level")}</div>
            <div className="relative">
              {chain.map((node, i) => (
                <button
                  key={node.name}
                  data-testid={`drilldown-level-${i}`}
                  onClick={() => setDepth(i)}
                  className="w-full text-left flex items-center gap-5 group py-1"
                >
                  <div className="flex flex-col items-center self-stretch">
                    <span className={`w-3 h-3 border transition-all duration-300 ${i === depth ? "bg-[#2563EB] border-[#2563EB] rotate-45" : "border-[#94A3B8] bg-transparent group-hover:border-[#2563EB]"}`} />
                    {i < chain.length - 1 && <span className="w-px flex-1 bg-[#E2DACD] my-1" />}
                  </div>
                  <div className={`flex-1 flex items-baseline justify-between border-b wp-rule pb-3 mb-3 transition-colors duration-300 ${i === depth ? "" : "opacity-60 group-hover:opacity-100"}`}>
                    <span className={`font-atlas text-xl md:text-2xl transition-colors duration-300 ${i === depth ? "text-[#2563EB]" : "text-[#0F2557]"}`}>{node.name}</span>
                    <span className="font-mono-wp text-[0.6rem] uppercase tracking-[0.2em] text-[#718096]">{node.level}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={path + depth}
                data-testid="drilldown-level-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="border border-[#BFD4F2] rounded-3xl bg-white p-8 lg:p-10 relative h-full shadow-lg shadow-blue-50"
              >
                <div className="absolute top-4 right-5 font-mono-wp text-[0.6rem] text-[#718096]">{active.coords}</div>
                <div className="wp-eyebrow">{active.level}</div>
                <h3 className="font-atlas text-3xl md:text-5xl text-[#0E1726] mt-3">{active.name}</h3>
                <div className="font-mono-wp text-xs text-[#4A5568] mt-2">{active.code} · {active.meta}</div>

                <div className="atlas-dash my-8" />

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <div className="font-mono-wp text-[0.6rem] uppercase tracking-[0.25em] text-[#718096]">{tr("Population")}</div>
                    <div className="font-atlas text-2xl md:text-3xl mt-1">{active.pop}</div>
                  </div>
                  <div>
                    <div className="font-mono-wp text-[0.6rem] uppercase tracking-[0.25em] text-[#718096]">{tr("Canonical path")}</div>
                    <div className="font-mono-wp text-xs text-[#0E1726] mt-2 leading-relaxed break-words">
                      {chain.slice(0, depth + 1).map((c) => c.name.toLowerCase().replace(/ /g, "-")).join(" / ")}
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="font-mono-wp text-[0.6rem] uppercase tracking-[0.25em] text-[#718096] mb-3">{tr("Active verticals at this level")}</div>
                  <div className="flex flex-wrap gap-2">
                    {active.verticals.map((v) => (
                      <span key={v} className="font-mono-wp text-[0.65rem] uppercase tracking-[0.15em] border border-[#BFD4F2] rounded-full px-3 py-1.5 text-[#4A5568] hover:border-[#16A34A] hover:text-[#16A34A] transition-colors duration-300">{tr(v)}</span>
                    ))}
                  </div>
                </div>

                <div className="mt-8">
                  <ShareBadge url={`https://worldpulse.pro/${chain.slice(1, depth + 1).map((c) => c.name.toLowerCase().replace(/ /g, "-")).join("/") || "world"}`} />
                </div>

                {depth === chain.length - 1 && (
                  <div className="mt-8 border-l-2 border-[#16A34A] pl-4 text-sm text-[#4A5568] italic font-atlas text-lg">
                    {tr("Every village gets the same engine as every nation.")}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
